import React, { useState, useRef } from 'react';
import { Box, Container, Typography, Chip, Paper } from '@mui/material';
import PageHeader from '../../components/PageHeader';
import FormatQuoteIcon from '@mui/icons-material/FormatQuote';
import PlayCircleIcon from '@mui/icons-material/PlayCircle';

const VIDEO_SRC = '/videos/rodrigo-ayala-promo.mp4';
const ALUMNI_URL = '/alumni';

export default function Testimonials() {
  const videoRef = useRef(null);
  const [playing, setPlaying] = useState(false);

  const handlePlay = async () => {
    if (!videoRef.current) return;
    try {
      await videoRef.current.play();
      setPlaying(true);
    } catch (err) {
      console.error('No se pudo reproducir el video:', err);
    }
  };

  return (
    <>
      <PageHeader
        title="Student Stories"
        subtitle="Hear directly from the students who chose American College and changed their future."
        breadcrumbs={[
          { label: 'Home', href: '/' },
          { label: 'About Us', href: '/about-us' },
          { label: 'Testimonials', href: '/about-us/testimonials' },
        ]}
      />

      <Container maxWidth="lg" sx={{ py: 8 }}>

        {/* ── FEATURED VIDEO ── */}
        <Box sx={{ mb: 10 }}>
          <Box sx={{ textAlign: 'center', mb: 5 }}>
            <Chip
              label="Featured Story"
              sx={{ backgroundColor: '#E31E2415', color: '#E31E24', fontWeight: 600, mb: 2 }}
            />
            <Typography variant="h4" sx={{ color: '#2B2B6B', fontWeight: 800, mb: 1 }}>
              Rodrigo Ayala's Story
            </Typography>
            <Typography sx={{ color: '#888', fontSize: '1rem' }}>
              Liberal Arts · Class of 2025 · Transferred to University of Tennessee
            </Typography>
          </Box>

          {/* Video player */}
          <Box
            sx={{
              position: 'relative',
              borderRadius: 4,
              overflow: 'hidden',
              boxShadow: '0 24px 64px rgba(43,43,107,0.2)',
              backgroundColor: '#0D1B2A',
              maxWidth: 360,
              mx: 'auto',
            }}
          >
            <video
              ref={videoRef}
              src={VIDEO_SRC}
              controls
              onPlay={() => setPlaying(true)}
              onPause={() => setPlaying(false)}
              style={{ width: '100%', display: 'block', borderRadius: 16 }}
            />

            {!playing && (
              <Box
                onClick={handlePlay}
                sx={{
                  position: 'absolute',
                  inset: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background:
                    'linear-gradient(135deg, rgba(43,43,107,0.7) 0%, rgba(227,30,36,0.5) 100%)',
                  cursor: 'pointer',
                  transition: 'background 0.3s ease',
                  '&:hover': {
                    background:
                      'linear-gradient(135deg, rgba(43,43,107,0.85) 0%, rgba(227,30,36,0.65) 100%)',
                  },
                }}
              >
                <PlayCircleIcon
                  sx={{
                    fontSize: 90,
                    color: 'white',
                    filter: 'drop-shadow(0 4px 16px rgba(0,0,0,0.4))',
                    transition: 'transform 0.2s ease',
                    '&:hover': { transform: 'scale(1.08)' },
                  }}
                />
                <Typography
                  sx={{
                    color: 'white',
                    fontWeight: 700,
                    fontSize: '1.1rem',
                    mt: 2,
                    letterSpacing: '0.5px',
                  }}
                >
                  Watch Rodrigo's Story
                </Typography>
              </Box>
            )}
          </Box>

          {/* Pull quote below video */}
          <Paper
            elevation={0}
            sx={{
              maxWidth: 680,
              mx: 'auto',
              mt: 4,
              p: 4,
              border: '1px solid #E0E0E0',
              borderRadius: 3,
              textAlign: 'center',
              position: 'relative',
            }}
          >
            <FormatQuoteIcon
              sx={{
                fontSize: 48,
                color: '#E31E2430',
                position: 'absolute',
                top: 12,
                left: 16,
              }}
            />
            <Typography
              sx={{
                color: '#444',
                fontSize: '1.1rem',
                fontStyle: 'italic',
                lineHeight: 1.8,
                px: 3,
              }}
            >
              "American College gave me the English foundation and academic confidence I needed
              to transfer to a top U.S. university. The faculty genuinely cared about my success
              every step of the way."
            </Typography>
            <Typography sx={{ color: '#2B2B6B', fontWeight: 700, mt: 2 }}>
              — Rodrigo Ayala
            </Typography>
          </Paper>
        </Box>

        {/* ── MORE STORIES → ALUMNI ── */}
        <Box sx={{ textAlign: 'center' }}>
          <Chip
            label="More Stories"
            sx={{ backgroundColor: '#2B2B6B15', color: '#2B2B6B', fontWeight: 600, mb: 2 }}
          />
          <Typography variant="h4" sx={{ color: '#2B2B6B', fontWeight: 800, mb: 1.5 }}>
            Meet more of our alumni
          </Typography>
          <Typography
            sx={{ color: '#666', fontSize: '1rem', maxWidth: 520, mx: 'auto', mb: 4 }}
          >
            Discover where our graduates are studying and working today.
          </Typography>
          <Box
            component="a"
            href={ALUMNI_URL}
            sx={{
              display: 'inline-block',
              backgroundColor: '#2B2B6B',
              color: 'white',
              fontWeight: 700,
              fontSize: '1rem',
              px: 5,
              py: 1.8,
              borderRadius: 2,
              textDecoration: 'none',
              boxShadow: '0 8px 24px rgba(43,43,107,0.3)',
              transition: 'all 0.25s ease',
              '&:hover': {
                backgroundColor: '#1a1a4a',
                transform: 'translateY(-2px)',
              },
            }}
          >
            View all alumni →
          </Box>
        </Box>

        {/* ── CTA ── */}
        <Paper
          elevation={0}
          sx={{
            mt: 10,
            p: { xs: 4, md: 6 },
            background: 'linear-gradient(135deg, #2B2B6B 0%, #1a1a4a 100%)',
            borderRadius: 3,
            textAlign: 'center',
          }}
        >
          <Typography
            variant="h5"
            sx={{ color: 'white', fontWeight: 800, mb: 1.5, fontSize: { xs: '1.3rem', md: '1.8rem' } }}
          >
            Ready to Write Your Own Story?
          </Typography>
          <Typography sx={{ color: 'rgba(255,255,255,0.7)', mb: 4, fontSize: '1rem' }}>
            Join hundreds of students who transformed their future at American College.
          </Typography>
          <Box
            component="a"
            href="/admissions"
            sx={{
              display: 'inline-block',
              backgroundColor: '#E31E24',
              color: 'white',
              fontWeight: 700,
              fontSize: '1rem',
              px: 5,
              py: 1.8,
              borderRadius: 2,
              textDecoration: 'none',
              boxShadow: '0 8px 24px rgba(227,30,36,0.4)',
              transition: 'all 0.25s ease',
              '&:hover': {
                backgroundColor: '#C41A1F',
                transform: 'translateY(-2px)',
              },
            }}
          >
            Start Your Application →
          </Box>
        </Paper>

      </Container>
    </>
  );
}